import axios from 'axios'
import router from '@/router/index'
import store from '@/store/index'
import { alert as $alert } from '@/utils/alert'
import storeManager from '@/utils/storeManager'
import { bootGate } from '@/utils/gate'
import { buildServiceRejection } from '@/utils/order'
import {
	clearMobilePreviewSession,
	getMobilePreviewSessionId,
	isMobilePreviewAuthenticationFailure,
	isMobilePreviewMode,
	recoverExpiredMobilePreviewSession,
	updateMobilePreviewSessionId,
} from '@/utils/mobilePreviewSession'
import {
	claimMissingRouteReload,
	isMissingRouteResponse,
} from '@/utils/missingRouteReload'

const set = axios.create({
	baseURL: `${process.env.VUE_APP_BASE_API}/api`,
	timeout: 60000,
})

const rejectServiceError = (response, fallbackMessage) =>
	Promise.reject(buildServiceRejection(response, fallbackMessage))

set.interceptors.request.use(
	async config => {
		try {
			await bootGate.whenReady('axios.js')
		} catch (error) {
			const requestError =
				error instanceof Error
					? error
					: new Error(typeof error === 'string' ? error : '')
			requestError.config = config
			throw requestError
		}

		const vuex = store.state
		const mobilePreviewSessionId = getMobilePreviewSessionId()
		config.mobilePreviewRequest = Boolean(mobilePreviewSessionId)
		const authToken = mobilePreviewSessionId || vuex.user.authToken
		delete config.headers['X-Auth-Token']
		if (authToken) {
			config.headers['Content-Type'] = 'application/json'
			config.headers['X-Auth-Token'] = authToken
		}

		const xSid = storeManager.storeId
		if (xSid) {
			config.headers['X-SID'] = xSid
		}

		const xCodeHash = storeManager.qrCodeHash
		if (xCodeHash) {
			config.headers['X-CODE-HASH'] = xCodeHash
		}

		config.headers['X-Front-Stage-Version'] = process.env.VUE_APP_VERSION
		return config
	},
	error => {
		return rejectServiceError({ config: error.config }, error.message)
	},
)

set.interceptors.response.use(
	response => {
		const authToken = response.headers['x-auth-token']
		if (authToken) {
			if (isMobilePreviewMode()) {
				updateMobilePreviewSessionId(authToken)
			} else {
				store.commit('user/SET_AUTH_TOKEN', authToken)
			}
		}
		return response.data
	},
	error => {
		if (error.message === 'Network Error' || error.code === 'ECONNABORTED') {
			return rejectServiceError(
				{ config: error.config },
				'網絡問題，請檢查您的網絡連接',
			)
		}
		const res = error.response
		if (!res) {
			return rejectServiceError({ config: error.config }, '無法獲取伺服器回應')
		}
		const rotatedPreviewSessionId = res.headers?.['x-auth-token']
		if (rotatedPreviewSessionId && isMobilePreviewMode()) {
			updateMobilePreviewSessionId(rotatedPreviewSessionId)
		}
		if (error.response?.config?.url === '/front/login') {
			return rejectServiceError(res)
		}
		// 必須搶在全域 401 處理之前：那裡會清 token 並導頁，遊客重新登入後 userId 可能改變、購物車對不上
		if (
			isMissingRouteResponse(
				res.config?.missingRouteKind,
				res.status,
				res.data?.errorCode,
			) &&
			claimMissingRouteReload(window.sessionStorage, Date.now())
		) {
			window.location.reload()
			// 頁面即將重新整理：不 resolve 也不 reject，呼叫端不會先跳錯誤提示
			return new Promise(() => {})
		}
		if (res.status === 503) {
			const title = res.data?.data?.title || '服務更新中'
			const message =
				res.data?.data?.message || res.data?.message || '系統維護中，請稍後再試'
			store.commit(
				'setMaintenance',
				{
					modal: true,
					title: title,
					message: message,
				},
				{ root: true },
			)
			return res.config?.rejectWithDetail
				? rejectServiceError(res, '服務更新中')
				: Promise.reject('服務更新中')
		}
		if (
			isMobilePreviewAuthenticationFailure({
				status: res.status,
				errorCode: res.data?.errorCode,
				mobilePreviewRequest: res.config?.mobilePreviewRequest,
			})
		) {
			clearMobilePreviewSession()
			store.commit('user/CLEAR_AUTH_TOKENS')
			if (res.config?.url === '/front/getPlatformInfo') {
				return set
					.request(res.config)
					.then(result =>
						recoverExpiredMobilePreviewSession().then(() => result),
					)
			}
			return recoverExpiredMobilePreviewSession().then(() =>
				set.request(res.config),
			)
		}
		if (res.status === 401) {
			store.commit('user/CLEAR_AUTH_TOKENS')

			// 特殊處理：初始化相關 API 的 401 錯誤交由調用方處理
			const url = error.response?.config?.url

			if (
				url === '/front/getCurrentBuyer' ||
				url === '/front/getPlatformInfo'
			) {
				return Promise.reject({
					status: 401,
					message: res.data.message || '未登入',
					errorCode: res.data.errorCode,
				})
			}

			return store
				.dispatch('guidePage')
				.then(() => rejectServiceError(res, '未登入'))
		}
		if (res.status === 403) {
			if (res.data.errorCode === 'FORBIDDEN') {
				store.commit('user/CLEAR_AUTH_TOKENS')

				return store
					.dispatch('guidePage')
					.then(() => rejectServiceError(res, '未登入'))
			} else {
				$alert('掃描異常，請確認QR code 未損壞並重新掃描', 'danger')
				// 店家合約已到期、合約已到期或已經被刪除、店家已刪除
				const currentHash = storeManager.qrCodeHash
				if (currentHash) {
					store.commit('user/REMOVE_STORE', currentHash)
				}
				const redirect = router.replace('/null/Error')
				if (res.config?.rejectWithDetail) {
					Promise.resolve(redirect).catch(() => {})
					return rejectServiceError(res)
				}
				return redirect
			}
		}

		return rejectServiceError(res)
	},
)

export default set
